import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <div className="max-w-md mx-auto px-6 py-20" data-testid="reset-invalid-token">
        <div className="brutal-card p-10">
          <h1 className="font-heading text-3xl font-black uppercase text-[#1A365D]">Invalid Link</h1>
          <p className="mt-3 text-slate-700">This reset link is missing or malformed. Request a new one.</p>
          <Link to="/forgot-password" className="btn-primary mt-6 inline-flex">Request New Link</Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    if (password !== confirm) { toast.error("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-md mx-auto px-6 py-20" data-testid="reset-success">
        <div className="brutal-card p-10">
          <CheckCircle2 className="text-[#C62828]" size={48} strokeWidth={1.5}/>
          <div className="label-tag mt-4">Password Updated</div>
          <h1 className="font-heading text-3xl md:text-4xl font-black uppercase mt-2 leading-none text-[#1A365D]">
            You're all set.
          </h1>
          <p className="mt-4 text-slate-700">Redirecting you to the login page…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-6 py-20" data-testid="reset-password-page">
      <div className="label-tag">Set New Password</div>
      <h1 className="font-heading text-4xl md:text-5xl font-black uppercase mt-2 leading-none text-[#1A365D]">
        Pick a new password.
      </h1>
      <p className="mt-2 text-slate-600">Choose something at least 6 characters long. You'll use it to sign in next time.</p>

      <form onSubmit={submit} className="brutal-card p-8 mt-8 space-y-5">
        <div>
          <label className="label-tag block mb-2">New Password</label>
          <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="ll-input" data-testid="reset-password-input"/>
        </div>
        <div>
          <label className="label-tag block mb-2">Confirm Password</label>
          <input type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="ll-input" data-testid="reset-confirm-input"/>
        </div>
        <button type="submit" className="btn-primary w-full" disabled={loading} data-testid="reset-submit-btn">
          {loading ? "Updating…" : "Update Password"}
        </button>
      </form>
    </div>
  );
}
